import React, { useEffect, useState, Dispatch } from 'react';
import { useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { IPasswordPolicy } from '@app/models';
import {
    PasslyValidatedPassword,
    ButtonSubmit,
    ErrorBlock,
    PasslyTextField
} from '@app/components';
import { onboardService } from '@app/services';
import { onPressEnterModal } from '@app/helpers';
import { toast } from 'react-toastify';

const initialState = {
    verifyPassword: '',
    validatedPassword: '',
    confirmPassword: '',
    errorMessage: '',
    validatedPasswordError: false,
    confirmPasswordError: false,
    passwordChangeTimedout: false
};

interface IReceivedData {
    isReset: boolean;
    passwordPolicy: IPasswordPolicy | undefined;
    setPasswordCallback: Function;
    setExpiredPasswordCallback: Function;
    isPasswordReset: boolean;
    setIsPasswordReset: Dispatch<boolean>;
}

const OnboardSetPasswordComponent = (props: IReceivedData) => {
    const { translation } = useTypedTranslation();
    const appCatalogLoading = useSelector((state: any) => state.launchPad.appCatalogLoading);

    const [{ validatedPassword, confirmPassword, errorMessage, confirmPasswordError, passwordChangeTimedout }, setFormState] = useState(initialState);
    const [disablePrimaryBtn, setDisablePrimaryBtn] = useState(true);
    const [validatedPasswordError, setValidatedPasswordError] = useState(false);

    useEffect(() => {
        if (confirmPassword !== '' && confirmPassword !== validatedPassword) {
            setFormState((previousState) => ({ ...previousState, confirmPasswordError: true }));
        } else {
            setFormState((previousState) => ({ ...previousState, confirmPasswordError: false }));
        }
    }, [confirmPassword, validatedPassword]);

    useEffect(() => {
        if (!validatedPasswordError && !confirmPasswordError && confirmPassword !== '' && validatedPassword !== '') {
            setDisablePrimaryBtn(false);
        } else {
            setDisablePrimaryBtn(true);
        }
    }, [validatedPasswordError, confirmPasswordError, confirmPassword, validatedPassword]);

    const onConfirmPasswordChange = (e: any) => {
        const { name, value } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: value }));

        if (value !== validatedPassword) {
            return false;
        }
    };

    const onValidatedPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        setFormState((previousState) => ({ ...previousState, validatedPassword: value }));
    };

    const setPassword = () => {
        props.setIsPasswordReset(true);
        setDisablePrimaryBtn(true);
        onboardService.setPasswordFromOnboarding(validatedPassword, handleError, handleSuccessSetPassword);
    };

    const handleSuccessSetPassword = (response: any) => {
        const status: number = response.status;

        if (status === 202) {
            setFormState((previousState) => ({ ...previousState, passwordChangeTimedout: true }));
        } else if (status === 204) {
            // completed
            if (props.isReset) {
                props.setExpiredPasswordCallback();
            } else {
                props.setPasswordCallback();
            }
        }
    };

    const handleError = (error: any) => {
        if (error.response.data.message) {
            toast(error.response.data.message);
        } else {
            toast('GENERIC_ERROR');
        }
        props.setIsPasswordReset(false);
        setDisablePrimaryBtn(false);
    };

    return (
        <>
            <div onKeyPress={onPressEnterModal(setPassword, disablePrimaryBtn)}>
                {props.isReset ? (
                    <>
                        <h1>{translation('ONBOARD_SETPASSWORD_EXPIRED')}</h1>
                        <p className='onboard__description onboard__description-top onboard__description-center'>
                            {translation('ONBOARD_SETPASSWORD_EXPIRED_LINE3')}
                        </p>
                    </>
                ) : (
                    <>
                        <h1>{translation('ONBOARD_SETPASSWORD_LINE1')}</h1>
                        <p className='onboard__description onboard__description-top onboard__description-center'>{translation('ONBOARD_SETPASSWORD_LINE2')}</p>
                    </>
                )}
                <ErrorBlock
                    className='onboard__error'
                    isError={passwordChangeTimedout || errorMessage !== ''}
                    errorMessage={passwordChangeTimedout ? translation('PASSWORD_CHANGE_QUEUE_TIMEOUT') : errorMessage !== '' ? errorMessage : ''}
                />


                <div className='form__row'>
                    <PasslyValidatedPassword
                        value={validatedPassword}
                        onChange={onValidatedPasswordChange}
                    />
                </div>

                <div className='form__row'>
                    <PasslyTextField
                        fullWidth
                        label={translation('CONFIRM_PASSWORD')}
                        value={confirmPassword}
                        onChange={onConfirmPasswordChange}
                        id='confirmPassword-input'
                        name='confirmPassword'
                        type='password'
                        error={confirmPasswordError}
                        helperText={confirmPasswordError && translation('PASSWORDS_DONT_MATCH')}
                        required
                    />
                </div>
            </div>
            <div className='onboard__buttons'>
                <ButtonSubmit
                    id='setPasswordButton'
                    onClick={setPassword}
                    className='onboard__button--center onboard__button--submit'
                    isDisabled={disablePrimaryBtn || appCatalogLoading}
                    isLoad={props.isPasswordReset}
                    buttonText={translation('NEXT')}
                />
            </div>
        </>
    );
};

export default OnboardSetPasswordComponent;
